import ContentCopy from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import WhatsApp from '@mui/icons-material/WhatsApp';
import XIcon from '@mui/icons-material/X';
import Alert from '@mui/material/Alert';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import { type MouseEvent, useId, useState } from 'react';

import { AppButton, MediaIconButton } from '@/components/atoms';
import { type VehicleListing } from '@/services/vehicles/types';
import { vehicleTitle } from '@/utils/vehicleFormat';

import {
  shareOrigin,
  vehicleShareMessage,
  vehicleShareUrl,
  vehicleWhatsappShareHref,
  vehicleXShareHref,
} from './vehicleShare';

type Props = {
  vehicle: VehicleListing;
  /** `media` is the round icon for a photograph; `button` is the labelled one. */
  variant?: 'media' | 'button';
  sx?: React.ComponentProps<typeof AppButton>['sx'];
};

/**
 * Share a car with WhatsApp, X, or by copying the full message. A menu rather
 * than the native share sheet, which cannot be limited to our channels and is
 * mostly absent on desktop. Each link is tagged with its channel.
 */
export function ShareVehicleButton({ vehicle, variant = 'media', sx }: Props) {
  const menuId = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [feedback, setFeedback] = useState<'copied' | 'failed' | null>(null);
  const open = anchor !== null;
  const label = `Share ${vehicleTitle(vehicle)}`;
  const close = () => setAnchor(null);

  const onOpen = (event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget);

  const copyMessage = async () => {
    close();
    try {
      if (!navigator.clipboard) throw new Error('No clipboard available.');
      const link = vehicleShareUrl(vehicle, 'copy', shareOrigin());
      await navigator.clipboard.writeText(vehicleShareMessage(vehicle, link));
      setFeedback('copied');
    } catch {
      setFeedback('failed');
    }
  };

  // Not gated on `open`: pressing a link closes the menu in the same event, and
  // a gated origin would blank the href before the browser follows it.
  const origin = shareOrigin();
  const aria = {
    'aria-haspopup': 'menu',
    'aria-controls': open ? menuId : undefined,
    'aria-expanded': open,
  } as const;

  return (
    <>
      {variant === 'media' ? (
        <MediaIconButton label={label} icon={<ShareIcon fontSize="small" />} onClick={onOpen} {...aria} />
      ) : (
        <AppButton
          appVariant="secondary"
          size="large"
          fullWidth
          startIcon={<ShareIcon />}
          onClick={onOpen}
          {...(sx ? { sx } : {})}
          {...aria}
        >
          Share
        </AppButton>
      )}

      <Menu id={menuId} anchorEl={anchor} open={open} onClose={close} slotProps={{ list: { 'aria-label': label } }}>
        <MenuItem
          component="a"
          href={vehicleWhatsappShareHref(vehicle, origin)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={close}
        >
          <ListItemIcon>
            <WhatsApp fontSize="small" />
          </ListItemIcon>
          <ListItemText>WhatsApp</ListItemText>
        </MenuItem>
        <MenuItem
          component="a"
          href={vehicleXShareHref(vehicle, origin)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={close}
        >
          <ListItemIcon>
            <XIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>X</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => void copyMessage()}>
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy message</ListItemText>
        </MenuItem>
      </Menu>

      <Snackbar
        open={feedback !== null}
        autoHideDuration={3000}
        onClose={(_, reason) => reason !== 'clickaway' && setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={feedback === 'failed' ? 'error' : 'success'}
          variant="filled"
          sx={{ color: '#fff', minWidth: { sm: 320 }, '& .MuiAlert-icon': { color: '#fff' } }}
        >
          {feedback === 'failed'
            ? 'Could not copy. Use the WhatsApp option or copy the address bar link.'
            : 'Message copied, ready to paste'}
        </Alert>
      </Snackbar>
    </>
  );
}
